/**
 * Formateadores y etiquetas de presentación de Clientes (US-2.3, es-CO).
 *
 * Los estados del semáforo (color + icono + texto) viven en `@goelectric/ui-components`;
 * acá solo se traducen enums del contrato a texto visible y se formatea moneda/fecha.
 */
import type {
  BalanceAdjustmentReason,
  CustomerDocumentType,
  PaymentMethodType,
} from '@goelectric/shared-types';

export const CUSTOMER_DOCUMENT_TYPE_LABELS: Record<CustomerDocumentType, string> = {
  CC: 'Cédula de ciudadanía',
  CE: 'Cédula de extranjería',
  NIT: 'NIT',
  PPT: 'Permiso por Protección Temporal',
};

export const PAYMENT_METHOD_TYPE_LABELS: Record<PaymentMethodType, string> = {
  CARD: 'Tarjeta',
  PSE: 'PSE',
  OTHER: 'Otro',
};

export const BALANCE_REASON_LABELS: Record<BalanceAdjustmentReason, string> = {
  MANUAL_CREDIT: 'Abono manual',
  MANUAL_DEBIT: 'Débito manual',
  REFUND: 'Reembolso',
  CORRECTION: 'Corrección',
  PROMOTION: 'Promoción',
};

/**
 * Formatea un monto en pesos colombianos (COP, sin decimales). El contrato expresa
 * los montos en COP; se muestra con el formato local (`$ 18.500`).
 */
export function formatCop(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return '—';
  }
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Monto con signo explícito para los ajustes de saldo: positivo = abono (`+$ 20.000`),
 * negativo = débito (`-$ 5.000`), cero sin signo.
 */
export function formatSignedCop(amount: number): string {
  const formatted = formatCop(Math.abs(amount));
  if (amount > 0) {
    return `+${formatted}`;
  }
  if (amount < 0) {
    return `-${formatted}`;
  }
  return formatted;
}

/** Fecha/hora corta local (es-CO) para el detalle. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
